// The app: the sign-in settings first (the API says which realm and client),
// then the session, the query cache and the router.
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { RouterProvider, createBrowserRouter } from 'react-router';

import { ApiError, request } from '../api/client';
import type { ClientConfigResponse } from '../api/generated/ClientConfigResponse';
import { SessionProvider } from '../auth/SessionProvider';
import { ErrorPanel, Loading } from '../design/components';
import { ClientConfigContext } from './config';
import { routes } from './routes';

export function createQueryClient(): QueryClient {
    return new QueryClient({
        defaultOptions: {
            queries: {
                staleTime: 10_000,
                // A refusal won't change on a retry; a dropped connection might.
                retry: (count, error) => count < 2 && !(error instanceof ApiError && error.status < 500),
                refetchOnWindowFocus: true,
            },
            mutations: { retry: false },
        },
    });
}

const router = createBrowserRouter(routes);

export function App() {
    const [config, setConfig] = useState<ClientConfigResponse | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [client] = useState(createQueryClient);
    useEffect(() => {
        request<ClientConfigResponse>('/api/v1/client-config').then(setConfig, setError);
    }, []);
    if (error !== null) {
        return (
            <main className="sf-page">
                <ErrorPanel error={error} title="The shop floor couldn’t start." />
            </main>
        );
    }
    if (config === null) {
        return (
            <main className="sf-page">
                <Loading what="the shop floor" />
            </main>
        );
    }
    return (
        <ClientConfigContext value={config}>
            <SessionProvider config={config}>
                <QueryClientProvider client={client}>
                    <RouterProvider router={router} />
                </QueryClientProvider>
            </SessionProvider>
        </ClientConfigContext>
    );
}
